/**
 * DOM Error Handler Utilities
 *
 * This module provides utilities to handle and debug DOM manipulation errors
 * that can occur during React development, especially with HMR and StrictMode.
 */

interface DOMErrorInfo {
  error: Error;
  timestamp: number;
  component?: string;
  stack?: string;
}

class DOMErrorHandler {
  private readonly errors: DOMErrorInfo[] = [];
  private readonly maxErrors = 10;

  /**
   * Check if an error is a DOM manipulation error
   */
  isDOMError(error: unknown): boolean {
    if (!(error instanceof Error)) {
      return false;
    }
    const domErrorPatterns = [
      'Node.removeChild',
      'Node.insertBefore',
      'not a child of this node',
      'Child to insert before is not a child',
      'The node to be removed is not a child',
    ];

    const message = error.message || '';
    return domErrorPatterns.some((pattern) => message.includes(pattern));
  }

  /**
   * Record a DOM error and log debugging context.
   *
   * Recovery is intentionally left to `ReactDOMErrorBoundary`: React still owns the
   * container and its fiber tree references the existing nodes, so tearing the DOM
   * down from here detaches nodes React later tries to remove, turning one
   * reconciliation error into a cascade.
   */
  handleError(error: Error, component?: string): void {
    if (!this.isDOMError(error)) {
      return;
    }

    const errorInfo: DOMErrorInfo = {
      error,
      timestamp: Date.now(),
      component,
      stack: error.stack,
    };

    // Add to error log
    this.errors.unshift(errorInfo);
    if (this.errors.length > this.maxErrors) {
      this.errors.pop();
    }

    // Log the error with context
    console.group(`🔧 DOM Error in ${component || 'Unknown Component'}`);
    console.error('Error:', error.message);
    console.warn('This is likely caused by React HMR or StrictMode double rendering');
    console.info('The React error boundary will attempt to recover automatically');

    if (process.env.NODE_ENV === 'development') {
      console.info('💡 Debugging tips:');
      console.info('1. Check for missing cleanup in useEffect hooks');
      console.info('2. Ensure event listeners are properly removed');
      console.info('3. Avoid direct DOM manipulation in React components');
      console.info('4. Use refs for DOM access instead of querySelector');
    }

    console.groupEnd();
  }

  /**
   * Get recent DOM errors for debugging
   */
  getRecentErrors(): DOMErrorInfo[] {
    return [...this.errors];
  }

  /**
   * Clear error log
   */
  clearErrors(): void {
    this.errors.length = 0;
  }

  /**
   * Get error statistics
   */
  getErrorStats(): { total: number; byComponent: Record<string, number> } {
    const byComponent: Record<string, number> = {};

    this.errors.forEach((error) => {
      const component = error.component || 'Unknown';
      byComponent[component] = (byComponent[component] || 0) + 1;
    });

    return {
      total: this.errors.length,
      byComponent,
    };
  }
}

// Create a singleton instance
export const domErrorHandler = new DOMErrorHandler();

/**
 * React hook for handling DOM errors in components
 */
export function useDOMErrorHandler(componentName: string) {
  const handleError = (error: Error) => {
    domErrorHandler.handleError(error, componentName);
  };

  return { handleError };
}

/**
 * Global error handler setup
 */
export function setupGlobalDOMErrorHandler(): void {
  // Handle unhandled errors
  window.addEventListener('error', (event) => {
    if (event.error && domErrorHandler.isDOMError(event.error)) {
      domErrorHandler.handleError(event.error, 'Global');
      event.preventDefault();
      return true;
    }
  });

  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    if (event.reason && domErrorHandler.isDOMError(event.reason)) {
      domErrorHandler.handleError(event.reason, 'Promise Rejection');
      event.preventDefault();
    }
  });

  // Expose error handler to global scope for debugging
  if (process.env.NODE_ENV === 'development') {
    (window as Window & { domErrorHandler?: typeof domErrorHandler }).domErrorHandler =
      domErrorHandler;
  }
}

export default domErrorHandler;
